// pages/monitor/panels/ems/EmsHome.js
// 《云平台（能源管家）需求文档》2.7.1 首页（p16–17）
// 差异 #4：能源管家首页无光伏功率、无充电桩、无充放电状态文字
import React from 'react';
import { useLang, t } from '../../consoles/ems/langContext';
import { Tag, Table } from 'antd';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import {
  RiseOutlined, ThunderboltOutlined, FallOutlined, DashboardOutlined, AccountBookOutlined,
} from '@ant-design/icons';
import { getOverview, getPowerCurve, getDevices, getStation } from '../../../../config/stationData';
import { Ef2NodeCard, ef2Pct } from '../../../../components/TopoGlbIcon';

const tick = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const grid = 'rgba(255,255,255,0.08)';
const tooltipStyle = {
  background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff',
};
const legendStyle = { color: 'rgba(255,255,255,0.7)', fontSize: 12 };

const COMM_TEXT = { normal: '正常', delay: '延迟', offline: '中断' };
const COMM_DOT = { normal: 'online', delay: 'warn', offline: 'offline' };

// 能量流转：连线 SVG；节点卡 HTML + 右侧 GLB（变压器暂留空）
function EnergyFlow({ gridPower, storagePower, loadPower }) {
  const { lang } = useLang();
  const W = 880;
  const H = 300;
  const busY = 150;
  const pct = (x, y, w, h) => ef2Pct(x, y, w, h, W, H);
  return (
    <div className="ef2-wrap" style={{ aspectRatio: `${W} / ${H}` }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="ef2-svg" role="img" aria-label="能量流转图">
        <defs>
          <marker id="ef2-arrow" markerWidth="9" markerHeight="9" refX="6" refY="4" orient="auto">
            <path d="M0,0 L8,4 L0,8 Z" fill="rgba(0,180,216,0.7)" />
          </marker>
        </defs>
        <line x1={140} y1={busY} x2={198} y2={busY} className="ef2-link" markerEnd="url(#ef2-arrow)" />
        <line x1={328} y1={busY} x2={388} y2={busY} className="ef2-link" markerEnd="url(#ef2-arrow)" />
        <line x1={388} y1={busY} x2={855} y2={busY} className="ef2-bus" />
        <text x={400} y={busY - 10} className="ef2-bus-label">{t(lang, '交流母线 400 V', 'AC Bus 400 V')}</text>
        <line x1={520} y1={busY} x2={520} y2={88} className="ef2-link" />
        <line x1={700} y1={busY} x2={700} y2={222} className="ef2-link" />
      </svg>
      <Ef2NodeCard
        name={t(lang, '电网', 'Grid')}
        value={gridPower}
        unit="kW"
        sub="10 kV"
        device="grid"
        style={pct(20, busY - 32, 120, 64)}
      />
      <Ef2NodeCard
        name={t(lang, '变压器', 'Transformer')}
        value="1250"
        unit="kVA"
        sub="10/0.4 kV"
        device={null}
        style={pct(198, busY - 32, 130, 64)}
      />
      <Ef2NodeCard
        name={t(lang, '储能单元', 'Storage Unit')}
        value={storagePower}
        unit="kW"
        sub={t(lang, 'PCS + 电池堆', 'PCS + Battery Packs')}
        device="storage"
        style={pct(430, 24, 180, 64)}
      />
      <Ef2NodeCard
        name={t(lang, '用电负荷', 'Load')}
        value={loadPower}
        unit="kW"
        sub={t(lang, '园区负载', 'Park Load')}
        device="load"
        style={pct(610, 222, 180, 60)}
      />
    </div>
  );
}

export default function EmsHome({ station }) {
  const { lang } = useLang();
  const st = station || getStation('xz');
  const id = st.id;
  const ov = getOverview(id);
  const curve = getPowerCurve(id);
  const dev = getDevices(id);
  const discharge = Number(ov.dischargeTotal);
  const charge = Number(ov.chargeTotal);
  // 差异 #1：综合效率 = 总放电量 / 总充电量
  const efficiency = charge > 0 ? ((discharge / charge) * 100).toFixed(1) : '—';

  // 差异文档 2.7.1（P16）：概览 6 项 = 实时有功功率 / 电池堆SOC / 总收益(万元) / 综合效率 / 总充电量 / 总放电量
  const incomeWan = (Number(ov.incomeTotal) / 10000).toFixed(2);
  const kpis = [
    { key: 'power', label: t(lang, '实时有功功率', 'Real-time Active Power'), value: ov.storagePower, unit: 'kW', icon: <ThunderboltOutlined />, foot: t(lang, '储能实时有功', 'Storage Real-time Active Power') },
    { key: 'soc', label: t(lang, '电池堆SOC', 'Battery Pack SOC'), value: ov.soc, unit: '%', icon: <DashboardOutlined />, foot: t(lang, '当前可用电量', 'Current Available Energy') },
    { key: 'income', label: t(lang, '总收益', 'Total Income'), value: incomeWan, unit: '万元', icon: <AccountBookOutlined />, foot: t(lang, '累计并网收益', 'Cumulative Grid Income') },
    { key: 'eff', label: t(lang, '综合效率', 'Overall Efficiency'), value: efficiency, unit: '%', icon: <RiseOutlined />, foot: t(lang, '总放电量 / 总充电量', 'Total Discharge / Total Charge') },
    { key: 'charge', label: t(lang, '总充电量', 'Total Charge'), value: charge, unit: 'MWh', icon: <RiseOutlined />, foot: t(lang, '累计充电量', 'Cumulative Charge') },
    { key: 'discharge', label: t(lang, '总放电量', 'Total Discharge'), value: discharge, unit: 'MWh', icon: <FallOutlined />, foot: t(lang, '累计放电量', 'Cumulative Discharge') },
  ];

  const last = curve[curve.length - 1] || {};
  const demandLimit = last.demandLimit;
  const reverseLimit = last.reverseLimit;

  // 告警状态：云站通信 / PCS / BMS / 电表 / 空调（文档 2.7.1 告警状态）
  const hvacInterrupted = dev.hvac.some((h) => h.state !== '正常');
  const meterNormal = dev.meters.every((m) => m.state === '正常');
  const commCards = [
    { key: 'cloud', name: t(lang, '云站通信', 'Cloud Comm'), comm: st.comm, alarm: '' },
    { key: 'pcs', name: t(lang, 'PCS', 'PCS'), comm: 'normal', alarm: dev.pcs.some((p) => p.alarm) ? `${dev.pcs.find((p) => p.alarm).name} ${t(lang, '直流侧绝缘偏低', 'DC Insulation Low')}` : '' },
    { key: 'bms', name: t(lang, 'BMS', 'BMS'), comm: 'normal', alarm: dev.racks.some((r) => r.alarm) ? `${dev.racks.find((r) => r.alarm).name} ${t(lang, '单体压差偏大', 'Large Cell Voltage Diff')}` : '' },
    { key: 'meter', name: t(lang, '电表', 'Meter'), comm: meterNormal ? 'normal' : 'delay', alarm: '' },
    { key: 'hvac', name: t(lang, '空调', 'AC Unit'), comm: hvacInterrupted ? 'delay' : 'normal', alarm: hvacInterrupted ? t(lang, 'AC-03 通信中断', 'AC-03 Comm Interrupted') : '' },
  ];

  const baseRows = [
    [t(lang, '电站名称', 'Station Name'), st.name], [t(lang, '电站类型', 'Station Type'), st.typeLabel], [t(lang, '所在区域', 'Location'), st.location],
    [t(lang, '投运日期', 'Commission Date'), st.onlineAt], [t(lang, '运行天数', 'Days in Operation'), `${st.age} 天`],
    [t(lang, '装机功率', 'Installed Power'), st.rated.power], [t(lang, '装机容量', 'Installed Capacity'), st.rated.capacity],
    [t(lang, '电压等级', 'Voltage Level'), st.grid.voltage], [t(lang, '变压器', 'Transformer'), st.grid.transformer], [t(lang, '运行策略', 'Operation Strategy'), st.strategyOn ? '已启用' : '未启用'],
  ];

  return (
    <div className="mod-body">
      {/* 概览数据 */}
      <div className="kpi-grid kpi-grid-5">
        {kpis.map((k) => (
          <div className="kpi-card" key={k.key}>
            <span className="kpi-icon">{k.icon}</span>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value">{k.value}<span className="unit"> {k.unit}</span></div>
            <div className="kpi-foot">{k.foot}</div>
          </div>
        ))}
      </div>

      {/* 站基本信息 */}
      <div className="panel">
        <div className="panel-title"><span>{t(lang, '电站基本信息', 'Station Basic Info')}</span></div>
        <div className="kpi-grid ef2-kv">
          {baseRows.map(([k, v]) => (
            <div className="dev-kv-row" key={k}>
              <span className="dev-kv-k">{k}</span>
              <span className="dev-kv-v">{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 能量流转图 */}
      <div className="panel">
        <div className="panel-title">
          <span>{t(lang, '能量流转图', 'Energy Flow Diagram')}</span>
          <small>{t(lang, '实时功率与综合效率', 'Real-time Power & Overall Efficiency')}</small>
        </div>
        <EnergyFlow gridPower={ov.gridPower} storagePower={ov.storagePower} loadPower={ov.loadPower} />
        <div className="grid-3" style={{ marginTop: 12 }}>
          <div className="strat-item"><div className="strat-item-label">{t(lang, '电网功率', 'Grid Power')}</div><div className="strat-item-value">{ov.gridPower}<span className="unit"> kW</span></div></div>
          <div className="strat-item"><div className="strat-item-label">{t(lang, '储能功率', 'Storage Power')}</div><div className="strat-item-value">{ov.storagePower}<span className="unit"> kW</span></div></div>
          <div className="strat-item"><div className="strat-item-label">{t(lang, '综合效率', 'Overall Efficiency')}</div><div className="strat-item-value">{efficiency}<span className="unit"> %</span></div></div>
        </div>
      </div>

      {/* 告警状态 */}
      <div className="panel">
        <div className="panel-title"><span>{t(lang, '告警状态', 'Alarm Status')}</span><small>{t(lang, '通信状态优先于告警状态展示', 'Comm Status Takes Priority Over Alarm')}</small></div>
        <div className="dev-status-grid">
          {commCards.map((c) => (
            <div className="dev-card" key={c.key}>
              <div className="dev-card-head">
                <span className={`dot ${COMM_DOT[c.comm]}`} />
                <span className="dev-name">{c.name}</span>
                <span className="dev-status">{t(lang, COMM_TEXT[c.comm])}</span>
              </div>
              <div className="dev-tags">
                {c.alarm ? <Tag color="orange" style={{ marginInlineEnd: 0 }}>{c.alarm}</Tag> : <Tag color="green" style={{ marginInlineEnd: 0 }}>{t(lang, '无告警')}</Tag>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 实时运行 */}
      <div className="panel">
        <div className="panel-title">
          <span>{t(lang, '实时运行', 'Real-time Operation')}</span>
          <small>{t(lang, '今日 0 点 ~ 次日 6 点 · 策略 / 储能 / 负载 / 电网功率与 SOC', 'Today 0:00–6:00 Next Day · Strategy / Storage / Load / Grid Power & SOC')}</small>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={curve} margin={{ top: 10, right: 20, bottom: 6, left: 0 }}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="clock" tick={tick} interval={11} minTickGap={24} stroke={grid} />
            <YAxis yAxisId="p" tick={tick} stroke={grid} unit="kW" width={54} />
            <YAxis yAxisId="s" orientation="right" domain={[0, 100]} tick={tick} stroke={grid} unit="%" width={44} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: '#fff' }} />
            <Legend wrapperStyle={legendStyle} />
            <ReferenceLine yAxisId="p" y={0} stroke="rgba(255,255,255,0.25)" />
            <ReferenceLine yAxisId="p" y={demandLimit} stroke="#ff5a7f" strokeDasharray="6 4" label={{ value: `${t(lang, '需量限制', 'Demand Limit')} ${demandLimit}`, fill: '#ff5a7f', fontSize: 11, position: 'insideTopRight' }} />
            <ReferenceLine yAxisId="p" y={reverseLimit} stroke="#f4c542" strokeDasharray="6 4" label={{ value: `${t(lang, '逆功率限制', 'Reverse Power Limit')} ${reverseLimit}`, fill: '#f4c542', fontSize: 11, position: 'insideBottomRight' }} />
            <Line yAxisId="p" type="stepAfter" dataKey="strategy" name={t(lang, '策略功率', 'Strategy Power')} stroke="#8b7cf6" strokeWidth={2} strokeDasharray="6 4" dot={false} />
            <Line yAxisId="p" type="stepAfter" dataKey="storage" name={t(lang, '储能功率', 'Storage Power')} stroke="#52e8b0" strokeWidth={2} dot={false} />
            <Line yAxisId="p" type="stepAfter" dataKey="load" name={t(lang, '负载功率', 'Load Power')} stroke="#ff5a7f" strokeWidth={2} dot={false} />
            <Line yAxisId="p" type="stepAfter" dataKey="grid" name={t(lang, '电网功率', 'Grid Power')} stroke="#4dd8ff" strokeWidth={2} dot={false} />
            <Area yAxisId="s" type="linear" dataKey="soc" name="SOC" stroke="#ffd166" fill="rgba(255,209,102,0.14)" strokeWidth={1.6} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="ems-note">
          {t(lang, `需量限制值 ${demandLimit} kW，逆功率限制值 ${reverseLimit} kW；当前 SOC ${last.soc}%。`, `Demand Limit ${demandLimit} kW, Reverse Power Limit ${reverseLimit} kW; Current SOC ${last.soc}%.`)}
        </div>
      </div>

      {/* 今日充放电量明细 */}
      <div className="panel">
        <div className="panel-title"><span>{t(lang, '今日充放电量', 'Today Charge/Discharge')}</span></div>
        <Table
          size="small"
          pagination={false}
          rowKey="k"
          columns={[
            { title: t(lang, '项目', 'Item'), dataIndex: 'k' },
            { title: t(lang, '今日数值', 'Today Value'), dataIndex: 'v', align: 'right' },
          ]}
          dataSource={[
            { k: t(lang, '充电量', 'Charge'), v: `${ov.chargeToday} kWh` },
            { k: t(lang, '放电量', 'Discharge'), v: `${ov.dischargeToday} kWh` },
            { k: t(lang, '可用电量', 'Available Energy'), v: `${ov.availableEnergy} kWh` },
            { k: t(lang, '当前 SOC', 'Current SOC'), v: `${ov.soc} %` },
          ]}
        />
      </div>
    </div>
  );
}
